import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, Download, Eye, Shield, Printer } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const NDAPage = () => {
  const navigate = useNavigate();
  const [ndaContent, setNdaContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Charger le contenu du NDA
    fetch('/NDA-SNE.md')
      .then(response => response.text())
      .then(text => {
        setNdaContent(text);
        setLoading(false);
      })
      .catch(error => {
        console.error('Erreur lors du chargement du NDA:', error);
        setLoading(false);
      });
  }, []);

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([ndaContent], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = 'NDA-SNE-Solutions-Numeriques-Enquetes.md';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
      {/* Header - Hidden on print */}
      <div className="print:hidden p-6 border-b border-gray-800">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/search')}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Entente de Non-Divulgation (NDA)
              </h1>
              <p className="text-gray-400 text-sm">Document confidentiel - Administration uniquement</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button
              onClick={handleDownload}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 print:p-0 print:max-w-none">
        <div className="bg-white text-gray-900 rounded-xl print:rounded-none shadow-xl print:shadow-none p-8 print:p-12">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : (
            <div className="prose prose-lg max-w-none nda-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1 className="text-2xl font-bold text-gray-900 text-center mb-2 print:text-3xl">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-lg font-bold text-gray-800 mt-8 mb-4 border-b-2 border-amber-500 pb-2 print:text-xl">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-base font-semibold text-gray-700 mt-4 mb-2">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-gray-700 mb-4 leading-relaxed">{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-bold text-gray-900">{children}</strong>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc ml-6 mb-4 space-y-2">{children}</ul>
                  ),
                  li: ({ children }) => (
                    <li className="text-gray-700">{children}</li>
                  ),
                  hr: () => (
                    <hr className="my-8 border-gray-300 print:my-6" />
                  ),
                }}
              >
                {ndaContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .nda-content {
            font-size: 11pt;
            line-height: 1.5;
          }
          .nda-content h1 {
            font-size: 18pt;
            margin-bottom: 0.5cm;
          }
          .nda-content h2 {
            font-size: 14pt;
            margin-top: 0.8cm;
            page-break-after: avoid;
          }
          .nda-content p, .nda-content li {
            page-break-inside: avoid;
          }
          @page {
            margin: 2cm;
            size: letter;
          }
        }
      `}</style>
    </div>
  );
};

export default NDAPage;
